"use client"

import { useInView } from "@/hooks/use-in-view";
import { AuroraBackground } from "@/components/ui/aurora-background";
import {
    ChefHat,
    Calendar,
    Sparkles,
    Users,
    ArrowRight
} from "lucide-react";
import Image from "next/image";

// >> ServiceCard component for clean card styling
interface ServiceCardProps {
    title: string;
    description: string;
    delay: string;
    isInView: boolean;
    icon: React.ComponentType<{ className?: string }>;
    href?: string;
}

function ServiceCard({ title, description, delay, isInView, icon: Icon, href }: ServiceCardProps) {
    const CardContent = () => (
        <div className={`group relative p-8 md:p-12 border border-white/10 bg-white/5 backdrop-blur-md shadow-xl rounded-lg transition-all duration-500 cursor-pointer hover:bg-white/20 hover:backdrop-blur-xl hover:shadow-2xl hover:scale-105 overflow-hidden animate-on-scroll h-full ${isInView ? `animate-fade-in-up ${delay}` : ''}`}>
            <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-br from-[#a19b92]/20 via-[#222]/10 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300" />
            <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 bg-white/10 rounded-full group-hover:bg-white/20 transition-colors duration-300">
                        <Icon className="w-6 h-6 text-white stroke-1" />
                    </div>
                    <h3 className={`uppercase tracking-wider text-white relative text-lg md:text-xl lg:text-2xl`}>
                        {title}
                    </h3>
                </div>
                <p className="text-[#a19b92] relative leading-relaxed mb-6 text-sm md:text-base">
                    {description}
                </p>
                <div className="inline-flex items-center gap-2 text-white hover:text-[#a19b92] transition-colors duration-200 font-medium mt-auto">
                    {href ? 'Zur Website' : 'Mehr Info'}
                    <ArrowRight className="w-4 h-4" />
                </div>
            </div>
        </div>
    );

    if (href) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" className="block h-full">
                <CardContent />
            </a>
        );
    }

    return <CardContent />;
}

export default function Services() {
    const [titleRef, titleInView] = useInView({ threshold: 0.3 });
    const [gridRef, gridInView] = useInView({ threshold: 0.2 });

    // >> Service data matching the clean card design
    const services = [
        {
            title: "Catering",
            description: "Mehr als Genuss – eine Inszenierung. Flying Dinner, Live-Stations, exklusive Menüfolgen: Wir verbinden Handwerk mit Ästhetik. Frisch, saisonal, individuell abgestimmt auf jeden Anlass. Vegan, vegetarisch, bio – wenn gewünscht, mit Haltung.",
            delay: "delay-100",
            icon: ChefHat
        },
        {
            title: "Business Events",
            description: "Starke Auftritte für Marken und Unternehmen. Von Produktlaunch über Kundenveranstaltung bis zur Mitarbeiterfeier inszenieren wir Business-Events, die wirken – präzise geplant, CI-konform gestaltet und kulinarisch auf höchstem Niveau.",
            delay: "delay-200",
            icon: Users
        },
        {
            title: "Events",
            description: "Jeder Raum wird Bühne, jeder Ablauf ein Erlebnis. Wir orchestrieren Events mit Charakter – präzise geplant, stilvoll umgesetzt. Anlässe: Hochzeiten, Jubiläen, Galas, Produktpräsentationen, Private Dinner",
            delay: "delay-300",
            icon: Calendar
        },
        {
            title: "Customized Solutions",
            description: "Einzigartig. Markenscharf. Sinnlich. Wir übersetzen Identität in Geschmack. CI-Buffets, vegane Luxuskonzepte, kuratierte Food-Stories. Für Marken, die nicht nur auffallen, sondern wirken wollen.",
            delay: "delay-400",
            icon: Sparkles
        }
    ];

    return (
        <AuroraBackground className="!p-0 h-auto min-h-[800px]">
            <section
                id="services"
                className="py-32 md:py-48 text-white relative overflow-hidden w-full z-10"
                aria-label="Unsere Services"
            >
                {/* Aurora-friendly dark overlay for depth */}
                <div className="absolute inset-0 bg-black/70 z-0 pointer-events-none"></div>
                {/* Background pattern for depth */}
                <div className="absolute inset-0 bg-[linear-gradient(45deg,#111111_25%,transparent_25%,transparent_75%,#111111_75%,#111111),linear-gradient(45deg,#111111_25%,transparent_25%,transparent_75%,#111111_75%,#111111)] bg-[length:60px_60px] opacity-5 z-10"></div>

                {/* Background Image (match About section sizing) */}
                <div className="absolute inset-0 z-15">
                    <div className="absolute inset-0 w-full h-full">
                        <Image
                            src="/images/13.jpg"
                            alt="Catering Background"
                            fill
                            className="object-cover"
                            sizes="100vw"
                        />
                        <div className="absolute inset-0 bg-black/40"></div>
                        {/* Soft fade from black at the top to blend from previous section */}
                        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 md:h-64 bg-gradient-to-t from-transparent via-black/70 to-black"></div>
                    </div>
                </div>

                <div className="container relative z-20">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6 max-w-7xl">
                        {/* Title Section */}
                        <div
                            ref={titleRef}
                            className={`lg:col-span-3 mb-8 animate-on-scroll ${titleInView ? 'animate-fade-in-up' : ''}`}
                        >
                            <h2 className={`text-3xl sm:text-4xl font-quicksand uppercase tracking-wide relative text-white`}>
                                <span className="font-adobe md:text-[5rem] text-[3rem] leading-none italic">Kulinarik auf höchstem Niveau.</span> <br />Erlebnisse mit Handschrift.
                            </h2>
                            <div className={`w-20 h-px bg-white my-6 ml-0 animate-on-scroll ${titleInView ? 'animate-slide-up-line delay-300' : ''}`}></div>
                            <p className={`mt-4 text-[#a19b92] max-w-3xl leading-relaxed animate-on-scroll ${titleInView ? 'animate-fade-in-up delay-400' : ''}`}>
                                Ob exklusive Privatfeier oder maßgeschneidertes Corporate Event – wir schaffen stilvolle Gesamtkonzepte, die weit über gutes Essen hinausgehen. Fein abgestimmt. Elegant inszeniert. <br /> Mit höchstem Anspruch.
                            </p>
                        </div>

                        {/* Service Cards */}
                        <div
                            ref={gridRef}
                            className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 mt-12 md:mt-24"
                        >
                            {services.map((service) => (
                                <ServiceCard
                                    key={service.title}
                                    title={service.title}
                                    description={service.description}
                                    delay={service.delay}
                                    isInView={gridInView}
                                    icon={service.icon}
                                    href={service.href}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </AuroraBackground>
    );
}
